import { router } from "@inertiajs/vue3"
import { DirectUpload } from "@rails/activestorage"
import type { ToastServiceMethods } from "primevue/toastservice"
import { ref } from "vue"

import { apolloClient } from "../queries/client"
import CREATE_DOCUMENT from "../queries/document/create.gql"

export function useDocumentUpload(
  toast: ToastServiceMethods,
  orderId: () => string,
) {
  const isUploading = ref(false)
  const uploadProgress = ref(0)
  const directUploadsProgress: ProgressEvent[] = []

  const directUploadDidProgress = (
    event: ProgressEvent,
    uploadIndex: number,
  ) => {
    directUploadsProgress[uploadIndex] = event

    const [loaded, total] = directUploadsProgress.reduce(
      (acc, { loaded, total }) => [(acc[0] += loaded), (acc[1] += total)],
      [0, 0],
    )
    uploadProgress.value = Math.round((loaded / total) * 100)
  }

  const uploadFile = (file: File, fileIndex: number) =>
    new Promise((resolve, reject) => {
      const upload = new DirectUpload(
        file,
        "/rails/active_storage/direct_uploads",
        {
          directUploadWillStoreFileWithXHR(request: XMLHttpRequest) {
            request.upload.addEventListener(
              "progress",
              (event: ProgressEvent) =>
                directUploadDidProgress(event, fileIndex),
            )
          },
        },
      )
      upload.create((error, blob) => {
        if (error) {
          toast.add({
            severity: "error",
            summary: `Failed to upload ${file.name}`,
          })
          reject(error)
        } else {
          resolve(blob!.signed_id)
        }
      })
    })

  const createDocuments = async (blobIds: string[]) => {
    let successCount = 0
    let failCount = 0

    for (const blobId of blobIds) {
      try {
        const result = await apolloClient.mutate({
          mutation: CREATE_DOCUMENT,
          variables: {
            orderId: orderId(),
            fileId: blobId,
          },
        })

        if (result?.data?.createDocument?.errors?.length) {
          failCount++
        } else {
          successCount++
        }
      } catch {
        failCount++
      }
    }

    return { successCount, failCount }
  }

  const uploadDocuments = async (files: File[], clearFiles: () => void) => {
    isUploading.value = true
    uploadProgress.value = 0
    directUploadsProgress.length = 0

    try {
      const uploads = files.map(uploadFile)
      const uploadResults = await Promise.allSettled(uploads)
      const uploadedBlobs = uploadResults.flatMap((result) =>
        result.status === "fulfilled" ? [result.value] : [],
      ) as string[]

      if (uploadedBlobs.length > 0) {
        const { successCount, failCount } = await createDocuments(uploadedBlobs)

        if (successCount > 0) {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: `${successCount} document${successCount > 1 ? "s" : ""} uploaded successfully`,
            life: 4000,
          })
        }

        if (failCount > 0) {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: `Failed to upload ${failCount} document${failCount > 1 ? "s" : ""}`,
            life: 5000,
          })
        }

        router.reload()
      }
    } catch (error) {
      console.error("Upload error:", error)
      toast.add({
        severity: "error",
        summary: "Upload failed",
        detail: "Please try again",
      })
    } finally {
      uploadProgress.value = 0
      directUploadsProgress.length = 0
      isUploading.value = false
      clearFiles()
    }
  }

  return {
    isUploading,
    uploadProgress,
    uploadDocuments,
  }
}
